
import { Stack, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { colors } from "../../../lib/teacup/lib/colors";
import { baseAtom, getUIStateIsOptionSelected, getValidFindLinks } from "../../stores/jotai/base";
import ThumbsUpIcon from '../../../icons/ThumbsUpIcon';
import ThumbsDownIcon from '../../../icons/ThumbsDownIcon';
import LinkIcon from '../../../icons/LinkIcon';
import CommentIcon, { ReadCommentIcon, UnReadCommentIcon } from '../../../icons/Comment';
import { aiIndicationIcons } from '../../../lib/teacup/lib/ai-indicator';

export default function MaterialBadgeBar(props: any) {
    const { entry, allComments,findingLinksData } = props;
    const { entryID, findingLinks, linkedEntryIDs, entryInsertionType } = entry;
    const base = useAtomValue(baseAtom);
    const theme = useTheme();

    const showLinkedHypothesisBadges = base
        ? getUIStateIsOptionSelected(base, "canvas_linked_view")
        : false;
    const showLinkedFindingBadges = base
        ? getUIStateIsOptionSelected(base, "canvas_insight_view")
        : false;
    const showAllComments = base
        ? getUIStateIsOptionSelected(base, "canvas_comment_view")
        : false;
        
    const showCanvasIndication = base
        ? getUIStateIsOptionSelected(base, "canvas_indication_view")
        : false;

    const commentCount = allComments?.length;
    // console.log("comments", allComments)

    const unreadCommentCount = useMemo(
        () =>
            allComments?.filter(
                (comment) => !comment?.isSeen && comment?.userID !== base?.user?.userID
            ).length,
        [allComments]
    );
    
    const greenFindingsLinkCount = useMemo(() => {
        
        const filteredFindings = findingLinks?.length>0 ? getValidFindLinks(findingLinksData, findingLinks) : [];
        return filteredFindings?.filter((finding) => finding?.supports)?.length;

    }, [findingLinks]);

    const redFindingsLinkCount = useMemo(() => {
        
        const filteredFindings = findingLinks?.length>0 ? getValidFindLinks(findingLinksData, findingLinks) : [];
        return filteredFindings?.filter((finding) => !finding?.supports)?.length;
        
    }, [findingLinks]);

    const linkedHypothesesCount = useMemo(() => {
        let entryIDs = linkedEntryIDs?.filter((id) => id !== entryID);

        entryIDs = new Set(entryIDs);

        return entryIDs?.size;
    }, [linkedEntryIDs, entryID]);

    const badgeItems = useMemo(() => {
        const items = [];

        if (showAllComments && commentCount > 0) {
            items.push({
                count: commentCount,
                icon: (
                    <ReadCommentIcon/>
                ),
            });
        }

        if (
            showLinkedFindingBadges &&
            (greenFindingsLinkCount > 0 || redFindingsLinkCount > 0)
        ) {
            items.push({
                count: greenFindingsLinkCount,
                icon: (
                    <ThumbsUpIcon  
                        sx={{
                            color: colors.CHARTS_GREEN,
                        }}
                    />
                ),
            });

            items.push({
                count: redFindingsLinkCount,
                icon: (
                    <ThumbsDownIcon 
                        sx={{
                            color: colors.CHARTS_RED,
                        }}
                    />
                ),
            });
        }

        if (showLinkedHypothesisBadges && linkedHypothesesCount > 0) {
            items.push({
                count: linkedHypothesesCount,
                icon: (
                    <LinkIcon
                        strokeWidth="2"
                        sx={{
                            color: colors.CANVAS_LINK_PURPLE,
                            fill:"none",
                            "&:hover": {
                                color: "#1976D2",
                            },
                        }}
                    />
                ),
            });
        }

        return items;
    }, [
        showLinkedHypothesisBadges,
        showLinkedFindingBadges,
        showAllComments,
        unreadCommentCount,
        commentCount,
        greenFindingsLinkCount,
        redFindingsLinkCount,
        linkedHypothesesCount,
    ]);

    const badgeHypothesesIndicatorItems = useMemo(() => {
        const items = [];

        if (showCanvasIndication && ['Human', 'AI', 'Combined'].includes(entryInsertionType)) {
            items.push({
                icon: aiIndicationIcons?.[entryInsertionType]
            });
        }

        return items;
    }, [
        entryInsertionType,
        showCanvasIndication
    ]);

    const unreadCommentBadge = () =>
        unreadCommentCount > 0 ? (
            <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                sx={{
                    backgroundColor: "white",
                    padding: "2px 10px",
                    borderRadius: "100px",
                    border: "1px solid rgb(130,130,130)",
                }}
            >
                <Typography
                    fontSize={14}
                    lineHeight={1}
                >
                    {unreadCommentCount}
                </Typography>
                <UnReadCommentIcon/>
            </Stack>
        ) : null;

    const generalBadge = () => (
        <Stack
            className="canvas-badge-bar"
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
                backgroundColor: theme?.custom?.white,
                padding: "2px 10px",
                borderRadius: "100px",
                border: "1px solid rgb(130,130,130)",
            }}
        >
            {badgeItems?.map((item) => (
                <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    key={item?.count}
                >
                    <Typography
                        fontSize={14}
                        lineHeight={1}
                    >
                        {item?.count}
                    </Typography>
                    {item.icon}
                </Stack>
            ))}
        </Stack>
    );

    const hypothesesIndicatorBadge = () => showCanvasIndication && (
        <Stack
            className="canvas-badge-bar"
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
                backgroundColor: theme?.custom?.white,
                padding: "2px 6px",
                borderRadius: "200px",
                border: "1px solid rgb(130,130,130)",
            }}
        >
            {badgeHypothesesIndicatorItems?.map((item,index) => (
                <Stack
                    key={index}
                    direction="row"
                    spacing={0.35}
                    alignItems="center"
                >
                    {item?.icon}
                </Stack>
            ))}
        </Stack>
    );

    return unreadCommentCount > 0 || badgeItems?.length > 0 || entryInsertionType!==null? (
        <Stack
            className="canvas-badge-bar"
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
                position: "absolute",
                right: "6px",
                top: "-2px",
                svg: {
                    fontSize: "16px",
                },
            }}
        >
            {unreadCommentCount > 0 && unreadCommentBadge()}
            {badgeItems.length > 0 && generalBadge()}
            {(showCanvasIndication && entryInsertionType!==null) &&  hypothesesIndicatorBadge()}
        </Stack>
    ) : null;
}
